import * as React from 'react'

import { Box, Form, Section, Text } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import InformationTooltip from 'modules/core/components/Inputs/InformationTooltip'
import Select from 'modules/core/components/Inputs/Select'
import TogglerCheckbox from 'modules/core/components/Inputs/TogglerCheckbox'
import helpCenterUrl from 'modules/core/lib/helpCenterUrl'
import i18n from 'modules/core/lib/i18n'

import type AllowanceForm from 'modules/timeoff/lib/AllowanceForm'
import PeriodForm from 'modules/timeoff/lib/PeriodForm'
import type { ITenurePeriod as PeriodAttributes } from 'modules/timeoff/lib/TenurePeriod'
import { tenurePeriodTransitionOptions } from 'modules/timeoff/lib/dynamicTranslations'
import Allowance from 'modules/timeoff/store/models/Allowance'

import FormModal from './FormModal'
import PeriodList from './PeriodList'

interface Props {
  allowanceForm: AllowanceForm
  isHourly: boolean
  hasProratedTenures: boolean
}

const TenurePeriods = ({ allowanceForm, isHourly, hasProratedTenures }: Props) => {
  const [newPeriodForm, setPeriodForm] = React.useState<PeriodForm | null | undefined>(null)

  const tenurePeriodTransitionSelectOptions = Object.keys(Allowance.TENURE_PERIOD_TRANSITION).map(
    (value) => ({
      label: tenurePeriodTransitionOptions(value),
      value,
    })
  )

  const tenureEnabledDescriptionKey = isHourly
    ? i18n.t('time_off.allowances.page.description.tenure_periods_enabled_hours')
    : i18n.t('time_off.allowances.page.description.tenure_periods_enabled_days')

  const enabled = allowanceForm.get('tenure_periods_enabled').value
  const openModal = () => setPeriodForm(new PeriodForm())
  const closeModal = () => setPeriodForm(null)

  const onSave = (payload: PeriodAttributes) => {
    const allowancePeriods = allowanceForm.get('tenure_periods')
    const periods = [payload, ...allowancePeriods.value]

    allowanceForm.setValues({
      tenure_periods: periods,
    })
    closeModal()
  }

  const baseAllowance = parseFloat(allowanceForm.get('holiday_allowance_in_cents').value) || 0
  const ptoTenuresEnabled = hasProratedTenures && allowanceForm.isTimeWorked

  return (
    <Section
      illustration="holidays-event-tenure"
      title={i18n.t('time_off.allowances.page.tenure_periods.title')}
      description={i18n.t('time_off.allowances.page.tenure_periods.description')}
      link={{
        href: helpCenterUrl('time_off_tenure_periods'),
        label: i18n.t('time_off.allowances.page.tenure_periods.link'),
      }}
      action={{
        label: i18n.t('time_off.allowances.page.button.add_tenure_period'),
        onClick: openModal,
        disabled: !enabled,
      }}
    >
      {!!newPeriodForm && (
        <FormModal
          periodForm={newPeriodForm}
          closeModal={closeModal}
          onSave={onSave}
          baseAllowance={baseAllowance}
          ptoTenuresEnabled={ptoTenuresEnabled}
          isHourly={isHourly}
          isNew
        />
      )}
      <TogglerCheckbox.ExpandableContainer open={enabled}>
        <Box>
          <TogglerCheckbox
            label={i18n.t('time_off.allowances.page.labels.tenure_periods_enabled')}
            field={allowanceForm.get('tenure_periods_enabled')}
          />
          <Box width="s300">
            <Text.Normal color="grey800" htmlTag="div">
              {tenureEnabledDescriptionKey}
            </Text.Normal>
          </Box>
        </Box>
        <Box>
          <Form>
            <Select
              field={allowanceForm.get('tenure_period_transition')}
              label={i18n.t('time_off.allowances.page.labels.tenure_period_transition')}
              secondaryLabel={
                <InformationTooltip
                  information={i18n.t(
                    'time_off.allowances.page.information.tenure_period_transition'
                  )}
                  icon="question"
                />
              }
              description={i18n.t('time_off.allowances.page.description.tenure_period_transition')}
              options={tenurePeriodTransitionSelectOptions}
              showOriginalLabelText
            />
          </Form>
          <PeriodList
            allowanceMaxBalanceCap={allowanceForm.maxBalanceCap}
            allowanceForm={allowanceForm}
            isHourly={isHourly}
            hasProratedTenures={hasProratedTenures}
          />
        </Box>
      </TogglerCheckbox.ExpandableContainer>
    </Section>
  )
}

export default observer(TenurePeriods)
